import React, { useState } from 'react';
import directorsData from '../data/directors.json';
import facultiesData from '../data/faculties.json';
import coachingData from '../data/coaching.json';

const tabs = [
  { id: 'introduction', label: 'About Savvy' },
  { id: 'directors', label: 'Leadership' },
];

const DIRECTOR_DISPLAY_ORDER = ['Prakash Verma', 'Rajesh Kumar', 'Deepak Kumar'];

const DEFAULT_ABOUT_TEXT = 'Savvy Academy is a Patna-based learning collective shaped by alumni of IIT, AIIMS, and IISc who are obsessed with building predictable success paths for JEE, NEET, and Olympiad aspirants from tier-2 and tier-3 cities.';
const DEFAULT_VISION_TEXT = 'To empower every learner from tier-2 and tier-3 cities with access to top-tier coaching, mentorship, and technology-enabled preparation for competitive examinations.';
const DEFAULT_MISSION_TEXT = 'Savvy Academy blends experienced faculty, rigorous practice, personal mentorship, and analytics-backed insights to create a predictable path to selections in JEE, NEET, and Olympiads.';
const DEFAULT_PEDAGOGY_POINTS = [
  'A curated curriculum built by IIT and AIIMS alumni with proven classroom results.',
  '360° support: live and recorded sessions, mentoring, peer learning, and parent reporting.',
  'AI-assisted assessment that highlights strengths, gaps, and priority chapters every week.',
  'Outcome-first culture driven by discipline, precision, and consistent feedback loops.',
];

const getValueByPath = (source, path) => {
  if (!source || path === undefined || path === null) {
    return undefined;
  }

  const segments = Array.isArray(path) ? path : String(path).split('.');
  return segments.reduce((acc, segment) => {
    if (acc === undefined || acc === null) {
      return undefined;
    }
    return acc[segment];
  }, source);
};

const pickCoachingField = (data, paths) => {
  const candidates = Array.isArray(paths) ? paths : [paths];

  for (let i = 0; i < candidates.length; i += 1) {
    const candidate = candidates[i];
    const value = getValueByPath(data, candidate);

    if (value !== undefined && value !== null && value !== '') {
      return value;
    }
  }

  return undefined;
};

const coerceRichTextValue = (value) => {
  if (value === undefined || value === null) {
    return '';
  }

  if (typeof value === 'string') {
    return value.trim();
  }

  if (Array.isArray(value)) {
    return value
      .map((entry) => coerceRichTextValue(entry))
      .filter(Boolean)
      .join('\n\n')
      .trim();
  }

  if (typeof value === 'object') {
    const candidates = [
      value.content,
      value.text,
      value.value,
      value.description,
      value.summary,
    ];

    for (let i = 0; i < candidates.length; i += 1) {
      const candidate = candidates[i];
      const coerced = coerceRichTextValue(candidate);
      if (coerced) {
        return coerced;
      }
    }

    return '';
  }

  return String(value).trim();
};

const extractTextField = (data, paths, fallback) => {
  const rawValue = pickCoachingField(data, paths);
  const text = coerceRichTextValue(rawValue);
  return text || fallback;
};

const parsePedagogyText = (input) => {
  const text = coerceRichTextValue(input);

  if (!text) {
    return [];
  }

  const blocks = text
    .split(/\r?\n\s*\r?\n/)
    .map((entry) => entry.trim())
    .filter(Boolean);

  if (blocks.length === 0) {
    return [];
  }

  const sections = [];
  let index = 0;

  while (index < blocks.length) {
    const block = blocks[index];
    const boldMatch = block.match(/^\*\*(.+?)\*\*$/);

    if (boldMatch) {
      const entry = {
        type: 'section',
        title: boldMatch[1].trim(),
        description: '',
        bullets: [],
      };

      index += 1;

      while (index < blocks.length) {
        const nextBlock = blocks[index];
        const nextIsBold = /^\*\*(.+?)\*\*$/.test(nextBlock);

        if (nextIsBold) {
          break;
        }

        if (/^[·•*-]+\s*/.test(nextBlock)) {
          entry.bullets.push(nextBlock.replace(/^[·•*-]+\s*/, '').trim());
        } else {
          entry.description = entry.description
            ? `${entry.description}\n\n${nextBlock}`
            : nextBlock;
        }

        index += 1;
      }

      if (entry.description) {
        entry.description = entry.description.trim();
      }
      entry.bullets = entry.bullets.filter(Boolean);

      sections.push(entry);
      continue;
    }

    sections.push({ type: 'intro', content: block });
    index += 1;
  }

  return sections;
};

const normalizePedagogy = (value, fallback) => {
  const primarySections = parsePedagogyText(value);

  if (primarySections.length > 0) {
    return primarySections;
  }

  return parsePedagogyText(fallback);
};

const buildLeadershipMembers = (faculties, directors) => {
  const facultyItems = Array.isArray(faculties) ? faculties : [];
  const directorItems = Array.isArray(directors) ? directors : [];
  const directorByName = new Map(directorItems.map((director) => [director.name, director]));

  const members = [];

  facultyItems.forEach((faculty) => {
    const directorInfo = faculty.director || directorByName.get(faculty.fullName) || null;

    if (!(faculty.isDirector || directorInfo)) {
      return;
    }

    const name = directorInfo?.name || faculty.fullName || '';

    if (!name) {
      return;
    }

    const subject = Array.isArray(faculty.subjects) && faculty.subjects.length > 0
      ? faculty.subjects[0].name
      : '';

    members.push({
      id: faculty.id || directorInfo?.id || name,
      name,
      photo: faculty.photo || directorInfo?.photo || directorByName.get(name)?.photo,
      designation: directorInfo?.designation || directorByName.get(name)?.designation || '',
      subject,
      profileDescription: coerceRichTextValue(faculty.profileDescription),
      fallbackMessages: directorInfo?.messages || directorByName.get(name)?.messages || [],
    });
  });

  directorItems.forEach((director) => {
    if (members.some((member) => member.name === director.name)) {
      return;
    }

    members.push({
      id: director.id,
      name: director.name,
      photo: director.photo,
      designation: director.designation,
      subject: '',
      profileDescription: '',
      fallbackMessages: director.messages || [],
    });
  });

  const getOrderIndex = (name) => {
    const index = DIRECTOR_DISPLAY_ORDER.indexOf(name);
    return index === -1 ? Number.MAX_SAFE_INTEGER : index;
  };

  return members
    .map((member) => ({
      ...member,
      profileDescription:
        member.profileDescription || coerceRichTextValue((member.fallbackMessages || []).join('\n\n')),
    }))
    .sort((a, b) => {
      const orderDiff = getOrderIndex(a.name) - getOrderIndex(b.name);

      if (orderDiff !== 0) {
        return orderDiff;
      }

      return a.name.localeCompare(b.name);
    });
};

const renderRichText = (text, keyPrefix) => {
  const content = coerceRichTextValue(text);

  if (!content) {
    return null;
  }

  const segments = content.split(/(\*\*[^*]+\*\*)/g).filter(Boolean);
  const nodes = [];

  segments.forEach((segment, index) => {
    const isBold = segment.startsWith('**') && segment.endsWith('**') && segment.length > 4;
    const contentSegment = isBold ? segment.slice(2, -2) : segment;
    const lines = contentSegment.split(/\n+/);

    if (isBold) {
      nodes.push(
        <strong key={`${keyPrefix}-bold-${index}`} className="text-primary">
          {lines.map((line, lineIdx) => (
            <React.Fragment key={`${keyPrefix}-bold-${index}-${lineIdx}`}>
              {line}
              {lineIdx < lines.length - 1 && <br />}
            </React.Fragment>
          ))}
        </strong>
      );
    } else {
      lines.forEach((line, lineIdx) => {
        nodes.push(
          <React.Fragment key={`${keyPrefix}-text-${index}-${lineIdx}`}>
            {line}
            {lineIdx < lines.length - 1 && <br />}
          </React.Fragment>
        );
      });
    }
  });

  return nodes;
};

const AboutPage = () => {
  const [activeTab, setActiveTab] = useState('introduction');

  const coachingName = coerceRichTextValue(coachingData?.name) || 'Savvy Academy';
  const aboutText = extractTextField(
    coachingData,
    ['about', 'about.content', 'about.description', 'about.summary', 'about.text', 'aboutText'],
    DEFAULT_ABOUT_TEXT
  );
  const visionText = extractTextField(
    coachingData,
    ['vision', 'vision.content', 'vision.description', 'vision.summary', 'vision.text'],
    DEFAULT_VISION_TEXT
  );
  const missionText = extractTextField(
    coachingData,
    ['mission', 'mission.content', 'mission.description', 'mission.summary', 'mission.text'],
    DEFAULT_MISSION_TEXT
  );
  const rawPedagogy = pickCoachingField(
    coachingData,
    ['pedagogy', 'pedagogy.points', 'pedagogy.items', 'pedagogy_list', 'pedagogy.points.items', 'pedagogyPoints', 'pedagogy_points']
  );
  const pedagogyFallback = Array.isArray(DEFAULT_PEDAGOGY_POINTS)
    ? DEFAULT_PEDAGOGY_POINTS.join('\n\n')
    : DEFAULT_PEDAGOGY_POINTS;
  const pedagogySections = normalizePedagogy(rawPedagogy, pedagogyFallback);

  const directors = Array.isArray(directorsData) ? directorsData : [];
  const faculties = Array.isArray(facultiesData) ? facultiesData : [];
  const leadershipMembers = buildLeadershipMembers(faculties, directors);

  return (
    <div className="min-h-screen bg-bg-default">
      <div className="page-container py-12">
        <div className="mb-12 space-y-4">
          <h1 className="text-4xl font-bold text-primary">{`About ${coachingName}`}</h1>
          <p className="text-text-secondary leading-relaxed">
            {renderRichText(aboutText, 'about-text')}
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-3 mb-10">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveTab(tab.id)}
              className={`px-6 py-2 rounded-full border transition-all duration-300 text-sm font-semibold ${
                activeTab === tab.id
                  ? 'bg-primary text-white border-primary shadow-md'
                  : 'bg-surface text-text-secondary border-border hover:bg-primary/5'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {activeTab === 'introduction' && (
          <section className="space-y-6">
            <div className="card p-8 bg-gradient-to-br from-primary/10 via-surface to-bg-default shadow-soft">
              <h2 className="text-xl font-semibold text-primary mb-3 tracking-tight">Vision</h2>
              <p className="text-text-secondary leading-relaxed">
                {renderRichText(visionText, 'vision-text')}
              </p>
            </div>

            <div className="card p-8 bg-gradient-to-br from-primary/10 via-surface to-bg-default shadow-soft">
              <h3 className="text-xl font-semibold text-primary mb-3 tracking-tight">Mission</h3>
              <p className="text-text-secondary leading-relaxed">
                {renderRichText(missionText, 'mission-text')}
              </p>
            </div>

            <div className="card p-8 bg-gradient-to-br from-primary/10 via-surface to-bg-default shadow-soft">
              <h3 className="text-xl font-semibold text-primary mb-5 tracking-tight">How we make it happen</h3>
              <div className="space-y-6 text-sm">
                {pedagogySections.map((section, index) => {
                  if (section.type === 'section') {
                    return (
                      <div key={`pedagogy-section-${section.title || index}`} className="space-y-3 text-text-secondary leading-relaxed">
                        <p className="text-base font-semibold text-primary tracking-tight">
                          {section.title}
                        </p>
                        {section.description && (
                          <p>{renderRichText(section.description, `pedagogy-desc-${index}`)}</p>
                        )}
                        {section.bullets.length > 0 && (
                          <ul className="space-y-2">
                            {section.bullets.map((bullet, bulletIdx) => (
                              <li key={`pedagogy-section-${section.title || index}-bullet-${bulletIdx}`} className="flex items-start gap-2">
                                <span className="mt-2 h-1.5 w-1.5 rounded-full bg-neutral-900"></span>
                                <span>{renderRichText(bullet, `pedagogy-bullet-${index}-${bulletIdx}`)}</span>
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    );
                  }

                  return (
                    <div key={`pedagogy-intro-${index}`} className="text-text-secondary leading-relaxed">
                      <p>{renderRichText(section.content, `pedagogy-intro-${index}`)}</p>
                    </div>
                  );
                })}
              </div>
            </div>
          </section>
        )}

        {activeTab === 'directors' && (
          <section>
            <div className="mb-8">
              <h2 className="text-2xl font-semibold text-primary">Meet the Leadership Team</h2>
            </div>

            <div className="space-y-6">
              {leadershipMembers.map((member) => {
                const initials = (member.name || '?')
                  .split(' ')
                  .filter(Boolean)
                  .slice(0, 2)
                  .map((part) => part.charAt(0).toUpperCase())
                  .join('');


                // Show designation chip (yellow) above subject chip if present
                const infoChips = [
                  member.subject ? { key: 'subject', label: `${member.subject} Faculty` } : null,
                ].filter(Boolean);

                return (
                  <article key={member.id || member.name} className="card p-6 bg-surface border border-border/60">
                    <div className="flex items-start gap-4">
                      {member.photo ? (
                        <img
                          src={member.photo}
                          alt={member.name || 'Leadership profile'}
                          className="h-20 w-20 rounded-full object-cover ring-4 ring-primary/20"
                        />
                      ) : (
                        <div className="h-20 w-20 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xl font-semibold ring-4 ring-primary/20">
                          {initials || 'SA'}
                        </div>
                      )}
                      <div>
                        <h3 className="text-lg font-semibold text-primary">{member.name}</h3>
                        {member.designation && (
                          <span className="inline-block bg-brand-yellow text-black text-xs font-semibold px-3 py-1 rounded-full mb-2">
                            {member.designation}
                          </span>
                        )}
                        {infoChips.length > 0 && (
                          <div className="mt-2 flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center">
                            {infoChips.map((chip) => (
                              <span
                                key={`${member.id || member.name}-${chip.key}`}
                                className="inline-flex items-center rounded-full px-2.5 py-0.5 text-[13px] font-semibold bg-brand-yellow text-text-primary shadow-soft max-w-fit whitespace-nowrap"
                              >
                                {chip.label && chip.label.replace(/([A-Z])/g, (m, c, i) => (i === 0 ? c.toUpperCase() : c.toLowerCase())).replace('Faculty', 'faculty')}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                    {member.profileDescription && (
                      <div className="mt-4 text-sm text-text-secondary leading-relaxed">
                        {renderRichText(member.profileDescription, `leadership-profile-${member.id || member.name}`)}
                      </div>
                    )}
                  </article>
                );
              })}
            </div>
          </section>
        )}
      </div>
    </div>
  );
};

export default AboutPage;